// C9 — People With Access section for the Share modal.
//
// Fetches /api/{kind}s/{id}/collaborators (C4 endpoint) on mount and
// after every mutation (invite / change-role / remove). Honors the
// backend visibility rules — the API already filters Viewer responses
// down to Owner + self, so this component just renders what arrives.
//
// Header shows live counts: "Editors (N) · Viewers (M)" (omitting any
// zero-count category to keep the header clean).
//
// Each non-owner row has a kebab menu whose options depend on context:
//   • Owner viewing someone else's row → Change to Editor/Viewer, Resend
//     (pending only), Remove.
//   • Self viewing own row (when not owner) → Leave.
//   • Otherwise the kebab is hidden.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, MoreHorizontal, RefreshCw, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { collabApi, ERROR_COPY } from '../../lib/collabApi';
import InviteCollaboratorModal from './InviteCollaboratorModal';

const isPending = (c) => c?.status === 'pending';

export default function PeopleWithAccessSection({
  resourceKind,           // "design" | "template"
  resourceId,
  resourceName,
  // Identity for the current viewer — drives visibility + kebab menu.
  currentUserId,
  isOwner,
  // Owner display (when the actor is not the owner the backend
  // already returns this in the listing payload's `owner` slot).
  ownerDisplayName,
}) {
  const [rows, setRows] = useState([]);
  const [ownerRow, setOwnerRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Per-row pending op so we can spin the affected row without
  // freezing the whole section.
  const [busyRowId, setBusyRowId] = useState(null);
  const [actionError, setActionError] = useState(null);

  // 403 from the listing endpoint is a normal outcome — backend
  // restricts the collaborator list to owner / editor / accepted
  // viewers. A regular user opening someone else's template's Share
  // modal will land here. We distinguish that "forbidden" case from
  // real errors so the UI renders a polite empty state instead of
  // a red error message.
  const [restricted, setRestricted] = useState(false);

  const refresh = useCallback(async () => {
    if (!resourceId || !resourceKind) return;
    setLoading(true);
    setLoadError(null);
    setRestricted(false);
    try {
      const data = await collabApi.list(resourceKind, resourceId);
      setOwnerRow(data?.owner || null);
      setRows(Array.isArray(data?.collaborators) ? data.collaborators : []);
    } catch (err) {
      if (err.status === 403 || err.code === 'forbidden' || err.code === 'not_authorized') {
        setRestricted(true);
        setRows([]);
        setOwnerRow(null);
      } else {
        setLoadError(err.message || 'Could not load collaborators.');
      }
    } finally {
      setLoading(false);
    }
  }, [resourceKind, resourceId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Counts in the header. We exclude removed/declined rows since the
  // backend listing already filters viewers to (owner + self), and
  // owner/editor see all statuses — pending Editor counts as Editor.
  const { editorCount, viewerCount } = useMemo(() => {
    let e = 0, v = 0;
    for (const r of rows) {
      if (r.status === 'removed' || r.status === 'declined') continue;
      if (r.role === 'editor') e += 1;
      else if (r.role === 'viewer') v += 1;
    }
    return { editorCount: e, viewerCount: v };
  }, [rows]);

  const handleChangeRole = useCallback(async (cid, newRole) => {
    setBusyRowId(cid);
    setActionError(null);
    try {
      await collabApi.changeRole(resourceKind, resourceId, cid, newRole);
      await refresh();
    } catch (err) {
      setActionError(ERROR_COPY[err.code] || err.message);
    } finally {
      setBusyRowId(null);
    }
  }, [resourceKind, resourceId, refresh]);

  const handleRemove = useCallback(async (cid) => {
    setBusyRowId(cid);
    setActionError(null);
    try {
      await collabApi.remove(resourceKind, resourceId, cid);
      await refresh();
    } catch (err) {
      setActionError(ERROR_COPY[err.code] || err.message);
    } finally {
      setBusyRowId(null);
    }
  }, [resourceKind, resourceId, refresh]);

  const handleResend = useCallback(async (cid) => {
    setBusyRowId(cid);
    setActionError(null);
    try {
      await collabApi.resend(resourceKind, resourceId, cid);
      await refresh();
    } catch (err) {
      setActionError(ERROR_COPY[err.code] || err.message);
    } finally {
      setBusyRowId(null);
    }
  }, [resourceKind, resourceId, refresh]);

  const handleSelfLeave = useCallback(async () => {
    setActionError(null);
    try {
      await collabApi.selfLeave(resourceKind, resourceId);
      await refresh();
    } catch (err) {
      setActionError(ERROR_COPY[err.code] || err.message);
    }
  }, [resourceKind, resourceId, refresh]);

  return (
    <section data-testid="people-with-access">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600">
          People With Access
          {loading && (
            <Loader2 className="inline w-3 h-3 ml-2 animate-spin text-gray-400" />
          )}
        </p>
        <p className="text-[11px] text-gray-500">
          {editorCount > 0 && <span data-testid="editor-count">Editors ({editorCount})</span>}
          {editorCount > 0 && viewerCount > 0 && <span> · </span>}
          {viewerCount > 0 && <span data-testid="viewer-count">Viewers ({viewerCount})</span>}
        </p>
      </div>

      {restricted ? (
        // Backend correctly hides the collaborator list from non-collab
        // non-owner viewers. Render a polite explanation rather than
        // a red error.
        <div
          className="text-sm border border-gray-100 rounded-md px-3 py-3 bg-gray-50"
          data-testid="people-restricted"
        >
          <p className="text-xs text-gray-600">
            Only the owner can see and manage collaborators on this resource.
          </p>
        </div>
      ) : (
        <ul className="text-sm divide-y divide-gray-100 border border-gray-100 rounded-md">
          <OwnerRow
            name={ownerRow?.collaborator_display_name || ownerDisplayName || 'Owner'}
            email={ownerRow?.collaborator_email}
            isYou={ownerRow?.collaborator_user_id === currentUserId}
          />
          {rows.map((r) => (
            <CollaboratorRow
              key={r.id}
              row={r}
              isOwnerActor={isOwner}
              isSelf={r.collaborator_user_id === currentUserId}
              busy={busyRowId === r.id}
              onChangeRole={handleChangeRole}
              onRemove={handleRemove}
              onResend={handleResend}
              onLeave={handleSelfLeave}
            />
          ))}
          {!loading && rows.length === 0 && (
            <li className="px-3 py-2.5 text-[11px] text-gray-400 italic">
              No collaborators yet.
            </li>
          )}
        </ul>
      )}

      {loadError && (
        <p className="text-[11px] text-red-600 mt-2" data-testid="people-load-error">
          {loadError}
        </p>
      )}
      {actionError && (
        <p className="text-[11px] text-red-600 mt-2" data-testid="people-action-error">
          {actionError}
        </p>
      )}

      {/* Invite is hidden for viewers — backend would 403 anyway,
          but we don't want to dangle the button. */}
      {isOwner && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setInviteOpen(true)}
          className="mt-2 w-full"
          data-testid="invite-people-btn"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Invite People
        </Button>
      )}

      <InviteCollaboratorModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        resourceKind={resourceKind}
        resourceId={resourceId}
        resourceName={resourceName}
        onInvited={refresh}
      />
    </section>
  );
}

function OwnerRow({ name, email, isYou }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-semibold">
        {(name || '?').trim().charAt(0).toUpperCase()}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate">
          {name} {isYou && <span className="text-[10px] text-gray-400">(You)</span>}
        </p>
        {email && (
          <p className="text-[11px] text-gray-500 truncate">{email}</p>
        )}
      </div>
      <span className="text-[11px] text-gray-500 font-medium">Owner</span>
    </li>
  );
}

function CollaboratorRow({
  row, isOwnerActor, isSelf, busy,
  onChangeRole, onRemove, onResend, onLeave,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const pending = isPending(row);
  // Removed rows shouldn't render — backend listing filters them in
  // the viewer view but owners/editors see them. We hide here so the
  // list stays focused; an "Activity" tab is a separate future feature.
  if (row.status === 'removed' || row.status === 'declined') return null;

  const displayName = row.collaborator_display_name
    || row.collaborator_email
    || 'Unknown';

  const showMenu = isOwnerActor || isSelf;

  return (
    <li className="flex items-center gap-3 px-3 py-2.5 relative" data-testid={`collab-row-${row.id}`}>
      <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-[10px] font-semibold">
        {(displayName || '?').trim().charAt(0).toUpperCase()}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate">
          {row.collaborator_display_name || row.collaborator_email}
          {isSelf && <span className="text-[10px] text-gray-400 ml-1">(You)</span>}
        </p>
        {row.collaborator_display_name && row.collaborator_email && (
          <p className="text-[11px] text-gray-500 truncate">{row.collaborator_email}</p>
        )}
      </div>

      <span className="text-[11px] text-gray-500 capitalize">
        {row.role}
        {pending && <span className="text-amber-600 ml-1">· pending</span>}
      </span>

      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}

      {showMenu && !busy && (
        <button
          type="button"
          className="ml-1 p-1 text-gray-400 hover:text-gray-700"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Row actions"
          data-testid={`collab-row-menu-${row.id}`}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      )}

      {menuOpen && (
        <div
          className="absolute right-2 top-9 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[180px]"
          onMouseLeave={() => setMenuOpen(false)}
        >
          {isOwnerActor && !pending && row.role === 'editor' && (
            <MenuButton
              onClick={() => { setMenuOpen(false); onChangeRole(row.id, 'viewer'); }}
              testId={`menu-make-viewer-${row.id}`}
            >
              Change to Viewer
            </MenuButton>
          )}
          {isOwnerActor && !pending && row.role === 'viewer' && (
            <MenuButton
              onClick={() => { setMenuOpen(false); onChangeRole(row.id, 'editor'); }}
              testId={`menu-make-editor-${row.id}`}
            >
              Change to Editor
            </MenuButton>
          )}
          {isOwnerActor && pending && (
            <MenuButton
              onClick={() => { setMenuOpen(false); onResend(row.id); }}
              testId={`menu-resend-${row.id}`}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2 inline" />
              Resend invitation
            </MenuButton>
          )}
          {isOwnerActor && (
            <MenuButton
              onClick={() => { setMenuOpen(false); onRemove(row.id); }}
              danger
              testId={`menu-remove-${row.id}`}
            >
              Remove
            </MenuButton>
          )}
          {isSelf && !isOwnerActor && !pending && (
            <MenuButton
              onClick={() => { setMenuOpen(false); onLeave(); }}
              danger
              testId={`menu-leave-${row.id}`}
            >
              Leave
            </MenuButton>
          )}
        </div>
      )}
    </li>
  );
}

function MenuButton({ children, onClick, danger = false, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full text-left px-3 py-1.5 text-xs',
        danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50',
      ].join(' ')}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
